import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'
import PageHeader from './PageHeader'
import Footer from './Footer'

const AdminFeedback = ({ onBack, onLogout, embedded = false }) => {
  const [feedback, setFeedback] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedFeedback, setSelectedFeedback] = useState(null)
  const [updating, setUpdating] = useState(null)

  useEffect(() => {
    loadFeedback()
  }, [statusFilter])

  const loadFeedback = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = statusFilter === 'all' ? {} : { status: statusFilter }
      const data = await apiService.listFeedback(params)
      setFeedback(data.feedback || [])
    } catch (err) {
      console.error('Error loading feedback:', err)
      setError(err.message || 'Failed to load feedback')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (feedbackId, newStatus) => {
    try {
      setUpdating(feedbackId)
      await apiService.updateFeedbackStatus(feedbackId, newStatus)
      // Update local state
      setFeedback(prev => prev.map(f =>
        f.feedback_id === feedbackId ? { ...f, status: newStatus } : f
      ))
      if (selectedFeedback?.feedback_id === feedbackId) {
        setSelectedFeedback(prev => ({ ...prev, status: newStatus }))
      }
    } catch (err) {
      console.error('Error updating feedback status:', err)
      setError(err.message || 'Failed to update status')
    } finally {
      setUpdating(null)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-300 border-blue-500/30'
      case 'reviewed': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
      case 'resolved': return 'bg-green-500/20 text-green-300 border-green-500/30'
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30'
    }
  }

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'bug':
        return (
          <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'feature':
        return (
          <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        )
      case 'improvement':
        return (
          <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        )
      case 'team_member':
        return (
          <svg className="w-5 h-5 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )
      default:
        return (
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        )
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // When embedded, render only the content without page wrapper
  const content = (
    <>
      {/* Filter Bar */}
        <div className="mb-6 flex items-center gap-4">
          <span className="text-sm text-gray-400">Filter by status:</span>
          <div className="flex gap-2">
            {['all', 'new', 'reviewed', 'resolved'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={loadFeedback}
            className="ml-auto px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : feedback.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-300 mb-2">No feedback yet</h3>
            <p className="text-gray-500">Feedback from users will appear here</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Feedback List */}
            <div className="lg:col-span-1 space-y-3">
              {feedback.map(item => (
                <div
                  key={item.feedback_id}
                  onClick={() => setSelectedFeedback(item)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedFeedback?.feedback_id === item.feedback_id
                      ? 'bg-purple-900/30 border-purple-500/50'
                      : 'bg-gray-800/50 border-gray-700/50 hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getCategoryIcon(item.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                        {item.team_member && (
                          <span className="text-xs text-pink-400 truncate">
                            {item.team_member}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white line-clamp-2 mb-1">
                        {item.summary || 'No summary available'}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{item.user_email}</span>
                        <span>•</span>
                        <span>{formatDate(item.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Feedback Detail */}
            <div className="lg:col-span-2">
              {selectedFeedback ? (
                <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden">
                  {/* Header */}
                  <div className="p-6 border-b border-gray-700/50">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          {getCategoryIcon(selectedFeedback.category)}
                          <span className="text-sm font-medium text-gray-300 capitalize">
                            {(selectedFeedback.category || 'General').replace('_', ' ')} Feedback
                          </span>
                          {selectedFeedback.team_member && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-pink-500/20 text-pink-300 border border-pink-500/30">
                              About {selectedFeedback.team_member}
                            </span>
                          )}
                        </div>
                        <p className="text-white text-lg font-medium">
                          {selectedFeedback.summary || 'Feedback details'}
                        </p>
                        <p className="text-sm text-gray-400 mt-1">
                          From {selectedFeedback.user_email} • {formatDate(selectedFeedback.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={selectedFeedback.status}
                          onChange={(e) => handleStatusUpdate(selectedFeedback.feedback_id, e.target.value)}
                          disabled={updating === selectedFeedback.feedback_id}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                        >
                          <option value="new">New</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Conversation */}
                  <div className="p-6">
                    <h4 className="text-sm font-medium text-gray-400 mb-4">Conversation</h4>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                      {selectedFeedback.conversation?.map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] px-4 py-2 rounded-xl ${
                              message.role === 'user'
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-700 text-gray-200'
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-800/30 rounded-2xl border border-gray-700/50 flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <svg className="w-12 h-12 text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-gray-400">Select a feedback item to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
    </>
  )

  // If embedded, just return the content
  if (embedded) {
    return content
  }

  // Otherwise, wrap in full page layout
  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      <PageHeader
        title="Feedback Admin"
        subtitle="View and manage user feedback"
        onLogout={onLogout}
        navItems={
          <button
            onClick={onBack}
            className="rounded-md bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            ← Back to Dashboard
          </button>
        }
      />
      <main className="flex-1 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-8">
        {content}
      </main>
      <Footer variant="authenticated" />
    </div>
  )
}

AdminFeedback.propTypes = {
  onBack: PropTypes.func,
  onLogout: PropTypes.func,
  embedded: PropTypes.bool
}

export default AdminFeedback
